import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  breakdown, formatPoints, pointsLabel, resolveConfig,
  type ChallengeConfig,
  conferirTroca, diaLocal, fimEfetivo, MOTIVO_DA_TROCA, PRECO_DO_VALE_FALTA, semanaDe,
} from "@nhomi/core";
import { watchConquistas, type Conquista } from "@/data/temporadas";
import { useTemporadaVigente } from "@/ui/Campeao";
import { trocarPontosPorVale } from "@/data/valeFalta";
import { useSession } from "@/lib/auth";
import { usePlano } from "@/lib/plano";
import { SeloAssinante } from "@/ui/primitives";
import { bloquear } from "@/data/moderacao";
import { Denunciar, type Alvo } from "@/ui/Denunciar";
import { removerDoDesafio, watchChallenge, type Challenge } from "@/data/challenges";
import { watchMembers, standings, type Member } from "@/data/members";
import { dayKey, monthPlates, toCorePlate, type Plate } from "@/data/plates";
import { Avatar, Empty, Spinner } from "@/ui/primitives";
import { StoragePhoto } from "@/ui/StoragePhoto";
import {
  IconBack, IconBlock, IconChevron, IconDots, IconFlag, IconFork,
} from "@/ui/icons";
import { TabBar } from "@/ui/TabBar";

const MESES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const DOW = ["D", "S", "T", "Q", "Q", "S", "S"];

const ABAS = [
  { id: "cal", label: "Calendário" },
  { id: "passaporte", label: "Passaporte" },
  { id: "pratos", label: "Pratos" },
  { id: "stats", label: "Stats" },
] as const;
type Aba = typeof ABAS[number]["id"];

/** Dias seguidos com prato ou vale-falta, contando de hoje para trás. */
function sequencia(dias: string[], coringas: string[] = []): number {
  const tem = new Set([...dias, ...coringas]);
  const cursor = new Date();
  if (!tem.has(dayKey(cursor))) cursor.setDate(cursor.getDate() - 1);
  let n = 0;
  while (tem.has(dayKey(cursor))) { n++; cursor.setDate(cursor.getDate() - 1); }
  return n;
}

export function Profile() {
  const { cid = "", uid = "" } = useParams();
  const { user } = useSession();
  const navigate = useNavigate();
  const souEu = uid === user?.uid;
  const { assinante } = usePlano();
  const [menu, setMenu] = useState(false);
  const [denuncia, setDenuncia] = useState<Alvo | null>(null);

  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [aba, setAba] = useState<Aba>("cal");
  const [cursor, setCursor] = useState(() => new Date());
  const [doMes, setDoMes] = useState<Plate[] | null>(null);

  useEffect(() => watchChallenge(cid, setChallenge), [cid]);
  useEffect(() => watchMembers(cid, setMembers), [cid]);
  const vigente = useTemporadaVigente(cid);

  async function remover(banir: boolean) {
    setMenu(false);
    const nome = members.find((m) => m.uid === uid)?.name ?? "essa pessoa";
    if (!confirm(banir
      ? `Remover ${nome} e não deixar voltar? Os pratos dela saem do feed deste grupo (continuam nos outros) e o convite deixa de funcionar pra ela.`
      : `Remover ${nome} do desafio? Os pratos dela saem do feed deste grupo (continuam nos outros). Ela pode voltar se receber o convite de novo.`)) return;
    try {
      await removerDoDesafio(cid, uid, banir);
      navigate(`/c/${cid}`, { replace: true });
    } catch (err) {
      alert((err as { message?: string }).message || "Não deu pra remover agora.");
    }
  }

  const ano = cursor.getFullYear();
  const mes = cursor.getMonth();

  useEffect(() => {
    let vivo = true;
    setDoMes(null);
    monthPlates(cid, uid, ano, mes)
      .then((l) => { if (vivo) setDoMes(l); })
      .catch(() => { if (vivo) setDoMes([]); });
    return () => { vivo = false; };
  }, [cid, uid, ano, mes]);

  const config: ChallengeConfig = useMemo(() => resolveConfig(challenge), [challenge]);
  const membro = members.find((m) => m.uid === uid);

  const placar = useMemo(() => standings(members), [members]);
  const posicao = placar.findIndex((m) => m.uid === uid) + 1;

  const mover = useCallback((delta: number) => {
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1));
  }, []);

  if (!membro) {
    return (
      <main className="screen">
        <div className="topbar">
          <Link className="topbar-btn" to={`/c/${cid}`} aria-label="Voltar"><IconBack /></Link>
          <div className="topbar-title" /><span className="topbar-btn" />
        </div>
        {members.length
          ? <Empty emoji="🤷" title="Participante não encontrado">
              <p>Talvez tenha saído do desafio.</p>
            </Empty>
          : <Spinner />}
        <TabBar cid={cid} />
      </main>
    );
  }

  const dias = membro.days ?? [];
  const pontos = placar.find((m) => m.uid === uid)?.totalPoints ?? 0;
  const streak = sequencia(dias, membro.jokerDays);
  const selos = membro.cuisineIds ?? [];

  return (
    <main className="screen">
      <div className="topbar">
        <Link className="topbar-btn" to={`/c/${cid}`} aria-label="Voltar"><IconBack /></Link>
        <div className="topbar-title" />
        {souEu
          ? <Link className="topbar-btn" to="/conta" aria-label="Conta">⚙</Link>
          /* Perfil alheio precisa de um caminho para denunciar e
             bloquear — as lojas cobram isso no PERFIL, não só no
             conteúdo solto. */
          : <button className="topbar-btn" onClick={() => setMenu((v) => !v)} aria-label="Mais">
              <IconDots />
            </button>}
      </div>

      {menu && !souEu && (
        <div className="card menu-flutuante list-card">
          <button className="list-row" onClick={() => {
            setMenu(false);
            setDenuncia({ tipo: "perfil", cid, pid: "", alvoUid: uid, oQue: "este perfil" });
          }}>
            <span className="ico"><IconFlag size={22} /></span>
            <span className="label">Denunciar perfil</span>
          </button>
          <button className="list-row danger" onClick={async () => {
            setMenu(false);
            if (!user) return;
            if (!confirm(
              `Bloquear ${membro.name}? Os pratos e comentários dessa pessoa `
              + "somem pra você. Ela não é avisada.")) return;
            await bloquear(user.uid, uid, membro.name);
            navigate(`/c/${cid}`, { replace: true });
          }}>
            <span className="ico"><IconBlock size={22} /></span>
            <span className="label">Bloquear {membro.name}</span>
          </button>
          {/* Só o dono, e nunca em si mesmo. Sem notificação: o grupo
              some da lista da pessoa. Ver `functions/src/membros.ts`. */}
          {challenge?.ownerUid === user?.uid && (
            <>
              <button className="list-row danger" onClick={() => void remover(false)}>
                <span className="ico emoji">🚪</span>
                <span className="label">Remover do desafio<span className="sub">pode voltar se receber o convite de novo</span></span>
              </button>
              <button className="list-row danger" onClick={() => void remover(true)}>
                <span className="ico emoji">⛔</span>
                <span className="label">Remover e não deixar voltar<span className="sub">o código de convite deixa de funcionar pra ela</span></span>
              </button>
            </>
          )}
        </div>
      )}

      {denuncia && user && (
        <Denunciar quem={user.uid} alvo={denuncia} aoFechar={() => setDenuncia(null)} />
      )}

      <div className="screen-body">
        <div className="profile-head">
          <Avatar name={membro.name} photo={membro.photo} size="hero"
            coroa={!!vigente?.campeoes.includes(uid)} />
          <div className="name">
            {membro.name}
            {/* O selo só aparece no PRÓPRIO perfil por enquanto: o plano
                mora em `users/{uid}` e o perfil de outra pessoa é
                desenhado a partir da ficha do desafio, que não carrega
                essa informação. Espalhar o plano para a ficha seria
                denormalizar um dado que muda por fora do desafio. */}
            {souEu && assinante && <SeloAssinante />}
          </div>
          {selos.length > 0 && (
            <div className="passport-strip">
              {selos.slice(0, 10).map((id) => (
                <span key={id}>{config.cuisines.find((c) => c.id === id)?.emoji ?? "🏅"}</span>
              ))}
              {selos.length > 10 && <span className="more">+{selos.length - 10}</span>}
            </div>
          )}
        </div>

        <div className="stats-row">
          <button className="stat" onClick={() => setAba("stats")}>
            <div className="v">{formatPoints(pontos)}</div><div className="k">Pontos</div>
          </button>
          <div className="stat">
            <div className="v">{dias.length}</div><div className="k">Dias ativos</div>
          </div>
          <button className="stat" onClick={() => setAba("cal")}>
            {/* Com o bônus ligado o número é o número; antes disso ele
                vira fração, porque o que falta é a informação que puxa e
                o total sozinho não diz que existe uma meta no sétimo dia. */}
            <div className="v">
              {streak >= config.rules.streakFrom
                ? `${streak} 🔥`
                : `${streak} / ${config.rules.streakFrom}`}
            </div>
            <div className="k">Sequência</div>
          </button>
        </div>

        <Trofeus cid={cid} uid={uid} />

        <div className="segmented on-white compact">
          {ABAS.map((a) => (
            <button key={a.id} className={a.id === aba ? "active" : ""} onClick={() => setAba(a.id)}>
              {a.label}
            </button>
          ))}
        </div>

        {aba === "cal" && (
          <Calendario cid={cid} ano={ano} mes={mes} dias={dias}
            coringas={membro.jokerDays ?? []} plates={doMes} onMover={mover} />
        )}

        {aba === "passaporte" && <Passaporte config={config} membro={membro} />}

        {aba === "pratos" && (
          <Grade cid={cid} ano={ano} mes={mes} plates={doMes} onMover={mover} />
        )}

        {aba === "stats" && (
          <Stats config={config} membro={membro} posicao={posicao} challenge={challenge} souEu={souEu} />
        )}

        <div className="gap" />
      </div>

      <TabBar cid={cid} />
    </main>
  );
}

/* ---------- calendário ---------- */

function Calendario(
  { cid, ano, mes, dias, coringas, plates, onMover }: {
    cid: string; ano: number; mes: number; dias: string[]; coringas: string[];
    plates: Plate[] | null; onMover: (d: number) => void;
  },
) {
  const porDia = new Map<string, Plate[]>();
  for (const p of plates ?? []) {
    const l = porDia.get(p.day);
    if (l) l.push(p); else porDia.set(p.day, [p]);
  }

  const total = new Date(ano, mes + 1, 0).getDate();
  const primeiroDiaDaSemana = new Date(ano, mes, 1).getDay();
  const hoje = dayKey();
  const temPrato = new Set(dias);
  const temCoringa = new Set(coringas);

  const celulas = [];
  for (let d = 1; d <= total; d++) {
    const chave = `${ano}-${String(mes + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    const doDia = porDia.get(chave) ?? [];
    const marcado = temPrato.has(chave);
    const coringa = !marcado && temCoringa.has(chave);
    const primeiro = doDia[0];

    // O primeiro dia é empurrado com grid-column-start; gerar células
    // vazias com aspect-ratio faz o navegador resolver a altura errado.
    const estilo = d === 1 && primeiroDiaDaSemana
      ? { gridColumnStart: primeiroDiaDaSemana + 1 }
      : undefined;

    const miolo = marcado ? (
      <span className="fill">
        {primeiro?.thumbPath
          ? <StoragePhoto path={primeiro.thumbPath} alt="" />
          : <IconFork size={18} />}
      </span>
    ) : coringa ? <span className="fill">🃏</span> : d;

    celulas.push(
      primeiro
        ? <Link key={chave} to={`/c/${cid}/p/${primeiro.id}`} style={estilo}
            className={`cal-cell hit ${chave === hoje ? "today" : ""}`}>{miolo}</Link>
        : <div key={chave} style={estilo}
            className={`cal-cell ${marcado ? "hit" : ""} ${coringa ? "joker" : ""} ${chave === hoje ? "today" : ""}`}>
            {miolo}
          </div>,
    );
  }

  return (
    <>
      <div className="cal-month">
        <button onClick={() => onMover(-1)} aria-label="Mês anterior"><IconBack size={20} /></button>
        <span>{MESES[mes]} {ano}</span>
        <button onClick={() => onMover(1)} aria-label="Próximo mês"><IconChevron size={20} /></button>
      </div>
      <div className="cal-dow">{DOW.map((d, i) => <span key={i}>{d}</span>)}</div>
      <div className="card"><div className="cal-grid">{celulas}</div></div>
      {plates === null && <Spinner />}
    </>
  );
}

/* ---------- passaporte ---------- */

function Passaporte({ config, membro }: { config: ChallengeConfig; membro: Member }) {
  const tem = new Set(membro.cuisineIds ?? []);

  /* Um selo apagado da configuração some da lista de opções, mas quem já
     carimbou continua com ele — e ele continua contando. Sem isso, apagar
     um selo faria o passaporte de alguém dizer 25/23. */
  const aposentados = [...tem]
    .filter((id) => !config.cuisines.some((c) => c.id === id))
    .map((id) => ({ id, label: id, emoji: "🏅", aposentado: true }));
  const todos = [
    ...config.cuisines.map((c) => ({ ...c, aposentado: false })),
    ...aposentados,
  ];

  return (
    <>
      <div className="passport-head">
        <div className="passport-count">{tem.size}<span>/{todos.length}</span></div>
        <div className="passport-label">carimbos no passaporte</div>
      </div>

      <div className="card"><div className="passport-grid">
        {todos.map((c) => (
          <div key={c.id}
            className={`stamp ${tem.has(c.id) ? "on" : ""} ${c.aposentado ? "aposentado" : ""}`}>
            <span className="flag">{c.emoji}</span>
            <span className="lbl">{c.label}</span>
          </div>
        ))}
      </div></div>

      <div className="hint-row">
        Cada selo novo que você posta carimba o passaporte. Escolha o selo na
        hora de publicar o prato.
        {aposentados.length > 0 && (
          <> {aposentados.length} {aposentados.length === 1
            ? "selo já não está" : "selos já não estão"} mais na lista do desafio,
            mas continuam contando pra você.</>
        )}
      </div>

      {config.events.length > 0 && (
        <>
          <div className="section-label left">Eventos</div>
          <div className="card breakdown">
            {config.events.map((ev) => {
              const guardado = membro.events?.[ev.id];
              const feitas = guardado?.cuisineIds?.length ?? 0;
              const fechado = (guardado?.bonus ?? 0) > 0;
              return (
                <div key={ev.id} className="bd-row">
                  <span className="bd-label">
                    {ev.emoji || "🎯"} {ev.name}
                    <div className="bd-conta">
                      {fechado
                        ? `fechado${guardado?.day ? ` em ${guardado.day}` : ""}`
                        : `${feitas}/${Math.max(1, ev.need)} selos`}
                    </div>
                  </span>
                  <span className="bd-valor">
                    {fechado ? `+${pointsLabel(guardado!.bonus)}` : pointsLabel(ev.bonus)}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>
  );
}

/* ---------- grade de fotos ---------- */

function Grade(
  { cid, ano, mes, plates, onMover }: {
    cid: string; ano: number; mes: number; plates: Plate[] | null; onMover: (d: number) => void;
  },
) {
  const lista = [...(plates ?? [])].sort((a, b) => (a.day < b.day ? 1 : -1));

  return (
    <>
      <div className="cal-month">
        <button onClick={() => onMover(-1)} aria-label="Mês anterior"><IconBack size={20} /></button>
        <span>{MESES[mes]} {ano}</span>
        <button onClick={() => onMover(1)} aria-label="Próximo mês"><IconChevron size={20} /></button>
      </div>
      {plates === null ? <Spinner /> : lista.length === 0 ? (
        <div className="comentarios-vazio">Nenhum prato nesse mês.</div>
      ) : (
        <div className="grade-fotos">
          {lista.map((p) => (
            <Link key={p.id} to={`/c/${cid}/p/${p.id}`}>
              <StoragePhoto path={p.thumbPath} alt={p.title} />
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

/* ---------- stats ---------- */

function Stats(
  { config, membro, posicao, challenge, souEu }: {
    config: ChallengeConfig; membro: Member; posicao: number; challenge: Challenge | null; souEu: boolean;
  },
) {
  const [plates, setPlates] = useState<Plate[] | null>(null);
  const { cid = "", uid = "" } = useParams();

  // O detalhamento precisa de TODOS os pratos da pessoa, não só do mês:
  // o bônus de sequência e os eventos atravessam a virada do mês.
  useEffect(() => {
    let vivo = true;
    const de = challenge?.startDay ?? "2000-01-01";
    const ate = fimEfetivo(challenge?.endDay);
    void (async () => {
      const { periodPlates } = await import("@/data/plates");
      const todos = await periodPlates(cid, de, ate).catch(() => []);
      if (vivo) setPlates(todos.filter((p) => p.uid === uid));
    })();
    return () => { vivo = false; };
  }, [cid, uid, challenge?.startDay, challenge?.endDay]);

  if (plates === null) return <Spinner />;

  const b = breakdown(config, plates.map(toCorePlate), {
    jokerDays: membro.jokerDays,
    extra: membro.extraPoints,
    // As missões como o servidor gravou: coletivas e de atividade dependem
    // do grupo inteiro, que esta tela não lê.
    eventosGravados: membro.events,
  });

  const dias = membro.days ?? [];
  const cozinhou = membro.homemadeCount || 0;
  const comprou = membro.boughtCount || 0;
  const taxaFogao = cozinhou + comprou
    ? Math.round((cozinhou / (cozinhou + comprou)) * 100) : 0;

  const linha = (k: string, v: string | number) => (
    <div className="bd-row"><span className="bd-label">{k}</span><span className="bd-valor">{v}</span></div>
  );

  const trocados = Object.values(membro.trocas ?? {}).reduce((a, v) => a + (v > 0 ? v : 0), 0);

  const eventosFechados = config.events
    .filter((e) => (membro.events?.[e.id]?.bonus ?? 0) > 0).length;

  return (
    <>
      <div className="section-label left">De onde vêm os pontos</div>
      <div className="card breakdown">
        {linha("👨‍🍳 Cozinhou", `${cozinhou} ${cozinhou === 1 ? "prato" : "pratos"}`)}
        {linha("🛒 Comprou", `${comprou} ${comprou === 1 ? "prato" : "pratos"}`)}
        {linha("Pontos dos pratos", pointsLabel(b.base))}
        {b.repeated > 0 && linha("🔁 Refeição repetida", `−${pointsLabel(b.repeatedLoss)}`)}
        {b.streakBonus > 0 && linha("🔥 Bônus de sequência", `+${pointsLabel(b.streakBonus)}`)}
        {b.guessed > 0 && linha("🎯 Cravou preço/caloria", `+${pointsLabel(b.guessed)}`)}
        {b.events > 0 && linha("🗓️ Eventos concluídos", `+${pointsLabel(b.events)}`)}
        {b.semFoto > 0 && linha("📝 Pratos sem foto", `−${pointsLabel(b.semFoto)}`)}
        {trocados > 0 && linha("🃏 Trocados por vale-falta", `−${pointsLabel(trocados)}`)}
        <div className="bd-row total">
          <span className="bd-label">Total</span>
          <span className="bd-valor">{pointsLabel(Math.round((b.total - trocados) * 100) / 100)}</span>
        </div>
      </div>

      <div className="card breakdown">
        {linha("Posição por pontos", posicao ? `${posicao}º` : "—")}
        {linha("Dias ativos", dias.length)}
        {linha("Sequência atual", `${sequencia(dias, membro.jokerDays)} dias`)}
      </div>

      <div className="section-label left">Cozinha</div>
      <div className="card breakdown">
        {linha("Taxa de fogão", `${taxaFogao}%`)}
        {linha("🌍 Carimbos no passaporte",
          `${(membro.cuisineIds ?? []).length}/${config.cuisines.length}`)}
        {linha("🎯 Eventos fechados", `${eventosFechados}/${config.events.length}`)}
        {linha("🃏 Vale-faltas restantes", membro.jokers ?? 0)}
        {/* O número existia desde o começo sem nada que o gastasse. Agora
            existe, e a linha diz onde — senão continua sendo um saldo que
            ninguém sabe sacar. */}
        <div className="hint-row">
          O convite pra usar aparece no feed do desafio na manhã seguinte
          a um dia sem prato.
        </div>
        {souEu && <TrocarPontos cid={cid} membro={membro} endDay={challenge?.endDay ?? ""} />}
      </div>
    </>
  );
}

/* ---------- troca de pontos ---------- */

/* A troca mora junto do saldo de vale-faltas porque é ali que a pessoa
   olha quando acabou o saldo. Some quando não dá — com o motivo, para
   ninguém achar que o botão sumiu por defeito. */
function TrocarPontos({ cid, membro, endDay }: { cid: string; membro: Member; endDay: string }) {
  const [ocupado, setOcupado] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const hoje = diaLocal(new Date(), "America/Sao_Paulo");
  const recusa = conferirTroca({
    totalPoints: membro.totalPoints ?? 0,
    jokers: membro.jokers ?? 0,
    trocas: membro.trocas ?? {},
    semana: semanaDe(hoje).de,
    hoje,
    endDay,
  });
  if (recusa === "desafio-acabou") return null;
  return (
    <div className="troca-pontos">
      <button className="btn btn-outline" disabled={!!recusa || ocupado}
        onClick={async () => {
          if (!window.confirm(`Trocar ${PRECO_DO_VALE_FALTA} pontos por 1 vale-falta? Sai do seu total, não do placar da semana.`)) return;
          setOcupado(true);
          setMsg(null);
          try {
            await trocarPontosPorVale(cid);
            setMsg("Pronto: +1 vale-falta.");
          } catch (e) {
            setMsg((e as { message?: string }).message || "Não deu pra trocar agora.");
          }
          setOcupado(false);
        }}>
        {ocupado ? "Um instante…" : `Trocar ${PRECO_DO_VALE_FALTA} pontos por 1 vale-falta`}
      </button>
      <div className="hint-row">
        {msg ?? (recusa ? MOTIVO_DA_TROCA[recusa] : "Uma troca por semana. Não mexe no placar da temporada.")}
      </div>
    </div>
  );
}

/* ---------- troféus ---------- */

/* O que fica de cada semana. Campeão de temporada é o troféu grande;
   missão fechada é a lembrança pequena. As duas vêm do registro que o
   servidor escreve — a ficha só guarda o estado de agora. */
function Trofeus({ cid, uid }: { cid: string; uid: string }) {
  const [lista, setLista] = useState<Conquista[] | null>(null);
  useEffect(() => watchConquistas(cid, uid, setLista), [cid, uid]);
  if (!lista?.length) return null;
  const titulos = lista.filter((c): c is Extract<Conquista, { tipo: "campeao" }> => c.tipo === "campeao")
    .sort((a, b) => (a.semana < b.semana ? 1 : -1));
  const missoes = lista.filter((c): c is Extract<Conquista, { tipo: "missao" }> => c.tipo === "missao")
    .sort((a, b) => (a.day < b.day ? 1 : -1));
  return (
    <div className="trofeus">
      {titulos.length > 0 && (
        <Link className="trofeus-item grande" to={`/c/${cid}/temporada/${titulos[0]!.semana}`}>
          <span className="trofeus-emoji">🏆</span>
          <span className="trofeus-txt">
            <strong>{titulos.length} {titulos.length === 1 ? "temporada vencida" : "temporadas vencidas"}</strong>
            <span>Última: semana de {titulos[0]!.semana.slice(8, 10)}/{titulos[0]!.semana.slice(5, 7)}</span>
          </span>
        </Link>
      )}
      {missoes.length > 0 && (
        <div className="trofeus-item">
          <span className="trofeus-emojis">{missoes.slice(0, 6).map((m) => m.emoji || "🎯").join(" ")}</span>
          <span className="trofeus-txt">
            <strong>{missoes.length} {missoes.length === 1 ? "missão fechada" : "missões fechadas"}</strong>
          </span>
        </div>
      )}
    </div>
  );
}
